import { useRequest } from 'ahooks';
import useFilter from './useFilter';
import { getPlatformData } from './service';
import { message, Tooltip } from 'antd';
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from 'react';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { initEcharts } from 'lib/echarts';
import { formatNumber, NumeralFormatPattern } from 'utils/numberFormat';
import { getChartMaxValue } from '../Activity/useActivityContents';

function Tiktok({
  platform,
  visible,
  departments,
  onUpdateTimeChange,
}: {
  platform: 'TIKTOK' | 'TIKTOK_US';
  visible: boolean;
  departments: Dashboard.Department[];
  onUpdateTimeChange: Dispatch<SetStateAction<string | undefined>>;
}) {
  const {
    t,
    storeId,
    range: [start, end],
    filter,
  } = useFilter({
    platform,
    visible,
    departments,
  });

  const { loading, data } = useRequest(
    () =>
      getPlatformData({
        storeId: storeId as any,
        startTime: start.format('YYYY-MM-DD HH:mm:ss'),
        endTime: end.format('YYYY-MM-DD HH:mm:ss'),
      }).then((res) => {
        if (res.length > 0) {
          onUpdateTimeChange((pre) => pre || res[0].dt);
        }
        return res
          .map(({ data, ...others }) => {
            return {
              ...others,
              data: Object.entries(JSON.parse(data)).reduce<any>((res, [key, value]) => {
                res[key] = isNaN(Number(value)) ? undefined : Number(value);
                return res;
              }, {}),
            };
          })
          .reverse();
      }),
    {
      ready: Boolean(storeId),
      refreshDeps: [storeId, start, end],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const { satisfactionPercentage, responsePercentage, supportSessionCount, responseTimeMins } =
    useMemo(() => {
      if (!data?.length) {
        return {};
      }
      const render = (field: string, formatter: NumeralFormatPattern = '0.00%', percent = 100) => {
        const list = data.map(({ data }) => data[field]).filter((_) => typeof _ === 'number');
        if (!list.length) {
          return undefined;
        }
        return formatNumber(
          list.reduce((res, cur) => res + cur, 0) / list.length / percent,
          formatter,
        );
      };
      return {
        satisfactionPercentage: render('satisfaction_percentage'),
        responsePercentage: render('response_percentage'),
        supportSessionCount: formatNumber(
          data.reduce((res, { data }) => res + (Number(data['support_session_count']) || 0), 0),
          '0,0',
        ),
        responseTimeMins: render('response_time_mins', '0.00', 1),
      };
    }, [data]);

  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data || !chartRef1.current || !chartRef2.current) {
      return;
    }

    const chart1 = initEcharts(chartRef1.current);
    const chart2 = initEcharts(chartRef2.current);

    chart1.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: [t('会话满意率'), t('24小时回复率')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '2%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map(({ countDt }) => countDt),
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: [
        {
          type: 'value',
          min: 0,
          max: 100,
          interval: 20,
        },
      ],
      series: [
        {
          name: t('会话满意率'),
          type: 'line',
          data: data.map(({ data }) => data.satisfaction_percentage),
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#5B8FF9',
          },
          tooltip: {
            valueFormatter: (value: any) =>
              typeof value === 'number' ? formatNumber(Number(value) / 100, '0.00%') : '-',
          },
        },
        {
          name: t('24小时回复率'),
          type: 'line',
          data: data.map(({ data }) => data.response_percentage),
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: '#F6BD16',
          },
          tooltip: {
            valueFormatter: (value: any) =>
              typeof value === 'number' ? formatNumber(Number(value) / 100, '0.00%') : '-',
          },
        },
      ],
    });

    const supportSessionCountList = data.map(({ data }) => data.support_session_count);
    const max1 = getChartMaxValue(
      supportSessionCountList.filter((_) => typeof _ === 'number'),
      5,
    );
    const responseTimeMins = data.map(({ data }) => data.response_time_mins);
    const max2 = getChartMaxValue(
      responseTimeMins.filter((_) => typeof _ === 'number'),
      5,
    );

    chart2.setOption({
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: [t('会话总数'), t('平均响应时长')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map(({ countDt }) => countDt),
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: [
        {
          type: 'value',
          min: 0,
          max: max1,
          splitNumber: 5,
          interval: max1 / 5,
        },
        {
          type: 'value',
          min: 0,
          max: max2,
          splitNumber: 5,
          interval: max2 / 5,
        },
      ],
      series: [
        {
          name: t('会话总数'),
          type: 'bar',
          barMaxWidth: '40px',
          data: supportSessionCountList,
          itemStyle: {
            color: '#5B8FF9',
          },
          tooltip: {
            valueFormatter: (value: any) => (typeof value === 'number' ? value : '-'),
          },
        },
        {
          name: t('平均响应时长'),
          type: 'bar',
          yAxisIndex: 1,
          barMaxWidth: '40px',
          data: responseTimeMins,
          itemStyle: {
            color: '#5AD8A6',
          },
          tooltip: {
            valueFormatter: (value: any) =>
              typeof value === 'number' ? `${value}${t('marketing.分钟')}` : '-',
          },
        },
      ],
    });

    const handleResize = () => {
      chart1.resize();
      chart2.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart1.dispose();
      chart2.dispose();
    };
  }, [data, t]);

  if (!data || !storeId) {
    return (
      <>
        {filter}
        {loading ? <LoadingOutlined /> : <></>}
      </>
    );
  }

  return (
    <>
      {filter}
      <section style={visible ? undefined : { display: 'none' }}>
        <header>
          <h3>{t('客户服务')}</h3>
        </header>
        <div>
          <ul>
            <dl>
              <dt>
                {t('会话满意率')}
                <Tooltip title={t('会话满意率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{satisfactionPercentage || '-'}</dd>
            </dl>
            <dl>
              <dt>
                {t('24小时回复率')}
                <Tooltip title={t('24小时回复率tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{responsePercentage || '-'}</dd>
            </dl>
          </ul>
          <section ref={chartRef1} />
          <ul style={{ marginTop: 16 }}>
            <dl>
              <dt>
                {t('会话总数')}
                <Tooltip title={t('会话总数tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{supportSessionCount || '-'}</dd>
            </dl>
            <dl>
              <dt>
                {t('平均响应时长')}
                <Tooltip title={t('平均响应时长tips')}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </dt>
              <dd>{responseTimeMins ? `${responseTimeMins} ${t('marketing.分钟')}` : '-'}</dd>
            </dl>
          </ul>
          <section ref={chartRef2} />
        </div>
      </section>
    </>
  );
}

export default Tiktok;
